import { useEffect, useState } from "react";
import {
  pairLabel,
  type AutopilotCandidate as Candidate,
  type AutopilotCheckVerdict,
  type AutopilotFilterCheck,
  type AutopilotGateMetric,
  type AutopilotRankedPool,
} from "@auto-pools/core";
import {
  formatAgo,
  formatPercent2,
  formatSpan,
  formatUsd,
  formatWhen,
  shorten,
} from "../lib/format";
import { refusalText } from "./autopilotRefusalText";
import { DexBadge } from "./DexBadge";
import { dexName, dexPoolUrl } from "../lib/dex";

const FILTER_LABEL: Record<AutopilotFilterCheck["filter"], string> = {
  pair: "Quote token",
  tvl: "TVL",
  price_freshness: "Price freshness",
  full_range: "Not full-range only",
  pool_status: "Pool status",
  dynamic_fee: "Fee mode",
  entry_fee: "Entry swap fee",
  pool_age: "Pool age",
  permissions: "Token permissions",
  effective_lps: "Effective LPs",
};

const GATE_LABEL: Record<AutopilotGateMetric["metric"], string> = {
  vol_ratio: "Volatility ratio",
  distance: "Distance from mean",
  trend: "Trend",
  elapsed_seconds: "Time since block",
  history_blocks: "Minute history",
};

function verdictSymbol(verdict: AutopilotCheckVerdict) {
  if (verdict === "pass") return { symbol: "✓", tone: "good" };
  if (verdict === "fail") return { symbol: "✕", tone: "bad" };
  if (verdict === "unavailable") return { symbol: "!", tone: "warn" };
  return { symbol: "·", tone: "muted" };
}

/**
 * The `pair` filter's value is `mintA/mintB` and its threshold a sentence meant for a server log,
 * not this compact column (review O3): show the pair the way the header already does, and a short
 * "USDC quote" threshold, instead of raw truncated mints and the server's raw threshold string.
 */
function filterValueText(check: AutopilotFilterCheck, pairText: string): string {
  if (check.value === null) return "—";
  switch (check.filter) {
    case "pair":
      return pairText;
    case "tvl":
      return formatUsd(check.value);
    case "price_freshness":
    case "pool_age":
      return formatSpan(Number(check.value) * 1000) ?? check.value;
    case "entry_fee":
      return formatPercent2(check.value);
    default:
      return check.value;
  }
}

function filterThresholdText(check: AutopilotFilterCheck): string {
  switch (check.filter) {
    case "pair":
      return "USDC quote";
    case "tvl":
      return `≥ ${formatUsd(check.threshold)}`;
    case "price_freshness":
      return `≤ ${formatSpan(Number(check.threshold) * 1000) ?? check.threshold}`;
    case "pool_age":
      return `≥ ${formatSpan(Number(check.threshold) * 1000) ?? check.threshold}`;
    case "entry_fee":
      return `≤ ${formatPercent2(check.threshold)}`;
    case "full_range":
      return `≠ ${check.threshold}`;
    case "effective_lps":
      return `≥ ${check.threshold}`;
    default:
      return check.threshold;
  }
}

function FilterRow({ check, pairText }: { check: AutopilotFilterCheck; pairText: string }) {
  const { symbol, tone } = verdictSymbol(check.verdict);
  return (
    <div className={`autopilot-filter-row ${tone}`}>
      <span className="autopilot-filter-name">{FILTER_LABEL[check.filter]}</span>
      <span className="num autopilot-filter-value">{filterValueText(check, pairText)}</span>
      <span className="num autopilot-filter-threshold">{filterThresholdText(check)}</span>
      <span className={`autopilot-filter-verdict ${tone}`} aria-hidden="true">
        {symbol}
      </span>
      <span className="sr-only">{check.verdict}</span>
    </div>
  );
}

/**
 * Chromium wraps a `<details>`'s body in an internal `::details-content` box that it collapses to
 * zero height with `content-visibility: hidden` whenever `open` is absent — a page stylesheet
 * cannot override that (it is UA `!important`), so "always expanded above 760 px" has to start from
 * an actually-open `<details>`, not a closed one forced open with CSS.
 */
function startsOpen(): boolean {
  if (typeof window === "undefined") return true;
  // jsdom and embedded views have no matchMedia; the cast keeps that possibility visible
  // (same pattern as BalanceSummary.tsx).
  const matchMedia = window.matchMedia as typeof window.matchMedia | undefined;
  if (!matchMedia) return true;
  return !matchMedia.call(window, "(max-width: 760px)").matches;
}

/**
 * Tracks the `(max-width: 760px)` breakpoint live (review D7): `startsOpen` alone only reads it
 * once at mount, so resizing from mobile to desktop left the disclosure closed even though its
 * `<summary>` toggle is hidden by CSS there, hiding every pool filter row with no way to reopen it.
 */
function useWideViewport(): boolean {
  const [wide, setWide] = useState(startsOpen);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const matchMedia = window.matchMedia as typeof window.matchMedia | undefined;
    if (!matchMedia) return;
    const media = matchMedia.call(window, "(max-width: 760px)");
    setWide(!media.matches);
    const update = (event: MediaQueryListEvent) => {
      setWide(!event.matches);
    };
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
    };
  }, []);
  return wide;
}

/**
 * ≤760 px (design 47, section 10): pool filters collapse behind an "n / n ✓" disclosure so the
 * always-visible entry-filter gauges below get the width. Above 760 px the `<summary>` is hidden by
 * CSS, so the disclosure must always be open there — including right after a resize into that
 * layout, not only at mount.
 */
function PoolFilters({ filters, pairText }: { filters: AutopilotFilterCheck[]; pairText: string }) {
  const wide = useWideViewport();
  const [open, setOpen] = useState(startsOpen);
  useEffect(() => {
    if (wide) setOpen(true);
  }, [wide]);
  const passed = filters.filter((check) => check.verdict === "pass").length;
  const tone = filters.some((check) => check.verdict === "fail")
    ? "bad"
    : filters.some((check) => check.verdict === "unavailable")
      ? "warn"
      : "good";
  const symbol = tone === "bad" ? "✕" : tone === "warn" ? "!" : "✓";
  // Built with concatenation, not a template literal: the guard below (R8) flags any `} / {`-shaped
  // text as a possible pair name, and this is a plain pass/total count, not one.
  const passedCount = String(passed) + " / " + String(filters.length);
  return (
    <details
      className="autopilot-filter-disclosure"
      open={open}
      onToggle={(event) => {
        // Keeps React's `open` in sync with the DOM's actual state (review D7): a native toggle
        // through the `<summary>` changes the attribute directly, and without this, React never
        // learns about it — so a later `setOpen(true)` from the resize effect below is a no-op
        // (the state was already `true`) and the manually-closed DOM never reopens.
        setOpen(event.currentTarget.open);
      }}
    >
      <summary>
        <span>Pool filters</span>
        <span className="autopilot-filter-summary">
          <span className="num">{passedCount}</span>
          <span className={tone}>{symbol}</span>
        </span>
      </summary>
      <div className="autopilot-filter-list" aria-label="Pool filters">
        {filters.map((check) => (
          <FilterRow key={check.filter} check={check} pairText={pairText} />
        ))}
      </div>
    </details>
  );
}

function gateValueText(metric: AutopilotGateMetric): string {
  if (metric.metric === "history_blocks") {
    if (metric.value === null) return "—";
    const blocks = Number(metric.value);
    return Number.isFinite(blocks)
      ? `${String(blocks)} block${blocks === 1 ? "" : "s"}`
      : metric.value;
  }
  return metric.value ?? "—";
}

function gateThresholdText(metric: AutopilotGateMetric): string {
  if (metric.metric === "history_blocks") {
    const blocks = Number(metric.threshold);
    // Design correction 1: "≥ 4 h (2 blocks)", never "needs 1,440" minutes.
    return Number.isFinite(blocks)
      ? `≥ ${String(blocks * 2)} h (${String(blocks)} blocks)`
      : metric.threshold;
  }
  return `skip ≥ ${metric.threshold}`;
}

/** A gauge track with amber (widen/skew) and red (skip) ticks at the server's own thresholds. */
function GateGauge({ metric }: { metric: AutopilotGateMetric }) {
  if (metric.metric === "history_blocks" || metric.value === null) return null;
  const threshold = Number(metric.threshold);
  const value = Math.abs(Number(metric.value));
  if (!Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(value)) return null;
  const scale = threshold * 1.2;
  const fillPercent = Math.min(100, (value / scale) * 100);
  const skipTick = Math.min(100, (threshold / scale) * 100);
  const adjustAt = metric.adjustAt !== null ? Number(metric.adjustAt) : null;
  const adjustTick =
    adjustAt !== null && Number.isFinite(adjustAt) ? Math.min(100, (adjustAt / scale) * 100) : null;
  return (
    <div className="autopilot-gauge" role="img" aria-label={`${GATE_LABEL[metric.metric]} gauge`}>
      <div className="autopilot-gauge-track">
        <div
          className={`autopilot-gauge-fill ${metric.verdict}`}
          style={{ width: `${String(fillPercent)}%` }}
        />
        {adjustTick !== null && (
          <span className="autopilot-gauge-tick warn" style={{ left: `${String(adjustTick)}%` }} />
        )}
        <span className="autopilot-gauge-tick bad" style={{ left: `${String(skipTick)}%` }} />
      </div>
    </div>
  );
}

function GateRow({ metric }: { metric: AutopilotGateMetric }) {
  const { symbol, tone } = verdictSymbol(metric.verdict);
  return (
    <div className={`autopilot-gate-row ${tone}`}>
      <div className="autopilot-gate-heading">
        <span className="autopilot-filter-name">{GATE_LABEL[metric.metric]}</span>
        <span className="num autopilot-filter-value">{gateValueText(metric)}</span>
        <span className="num autopilot-filter-threshold">{gateThresholdText(metric)}</span>
        <span className={`autopilot-filter-verdict ${tone}`} aria-hidden="true">
          {symbol}
        </span>
        <span className="sr-only">{metric.verdict}</span>
      </div>
      <GateGauge metric={metric} />
    </div>
  );
}

/** A failed permissions check names why (a fixed server text); a bare "fail" would not say. */
function permissionsReason(refusal: AutopilotFilterCheck): string {
  return refusal.filter === "permissions" && refusal.verdict === "fail" && refusal.value !== null
    ? ` · ${refusal.value}`
    : "";
}

function RankedRow({ ranked, checked }: { ranked: AutopilotRankedPool; checked: boolean }) {
  return (
    <li className="autopilot-ranked-row">
      <span className="num">{shorten(ranked.pool.address, 4)}</span>
      <DexBadge dex={ranked.pool.dex} />
      <span>{pairLabel(ranked.pool.tokenA, ranked.pool.tokenB)}</span>
      <span className="num">
        {ranked.lpFeesTvlPercent === null ? "—" : formatPercent2(ranked.lpFeesTvlPercent)}
      </span>
      <span
        className={ranked.refusal ? "autopilot-ranked-reason bad" : "autopilot-ranked-reason muted"}
      >
        {ranked.refusal
          ? `${FILTER_LABEL[ranked.refusal.filter]} · ${ranked.refusal.verdict}${permissionsReason(ranked.refusal)}`
          : checked
            ? "passed"
            : "not checked"}
      </span>
    </li>
  );
}

export function AutopilotCandidate({ candidate }: { candidate: Candidate | null }) {
  if (!candidate) {
    return (
      <section className="panel autopilot-candidate" aria-label="Autopilot candidate">
        <p className="autopilot-candidate-empty">No scan has run yet.</p>
      </section>
    );
  }
  return (
    <section className="panel autopilot-candidate" aria-label="Autopilot candidate">
      <header className="autopilot-candidate-header">
        <div>
          <strong>
            {candidate.pool
              ? pairLabel(candidate.pool.tokenA, candidate.pool.tokenB)
              : "No candidate"}
          </strong>
          {candidate.pool && <DexBadge dex={candidate.pool.dex} />}
          <small>
            {candidate.scan.passed} of {candidate.scan.ranked} pools passed every filter · evaluated{" "}
            {formatAgo(candidate.evaluatedAt)}
            {!candidate.scan.complete && " · scan incomplete"}
          </small>
        </div>
        {candidate.pool && (
          <a
            className="pool-link"
            href={dexPoolUrl(candidate.pool.dex, candidate.pool.address)}
            target="_blank"
            rel="noreferrer"
          >
            {dexName(candidate.pool.dex)} pool
          </a>
        )}
      </header>
      {candidate.pool && (
        <PoolFilters
          filters={candidate.filters}
          pairText={pairLabel(candidate.pool.tokenA, candidate.pool.tokenB)}
        />
      )}
      {candidate.gate.length > 0 && (
        <div className="autopilot-gate-list" aria-label="Entry filter">
          {candidate.gate.map((metric) => (
            <GateRow key={metric.metric} metric={metric} />
          ))}
        </div>
      )}
      {candidate.entry && (
        <p className="autopilot-entry-summary">
          Entering at width {candidate.entry.widthPercent}% · {candidate.entry.skew} ·{" "}
          {candidate.entry.belowPercent}% / +{candidate.entry.abovePercent}%
        </p>
      )}
      {candidate.refusal && (
        <p className="autopilot-refusal" role="status">
          {refusalText(candidate.refusal)}
          {candidate.refusal.retryAt && ` · ranks again ${formatWhen(candidate.refusal.retryAt)}`}
        </p>
      )}
      {(candidate.rankedAbove.length > 0 || candidate.rankedBelow.length > 0) && (
        <details className="autopilot-ranked">
          <summary>Ranking ({candidate.scan.ranked} pools)</summary>
          <ul>
            {candidate.rankedAbove.map((ranked) => (
              <RankedRow key={ranked.pool.address} ranked={ranked} checked />
            ))}
            {candidate.rankedBelow.map((ranked) => (
              <RankedRow key={ranked.pool.address} ranked={ranked} checked={false} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
